'use client';

import { useEffect } from 'react';
import { AlertOctagon, RefreshCw, Loader2 } from 'lucide-react';
import { globalErrorHandler } from '@/lib/utils/global-error-handler';
import { recordingSafeReload } from '@/lib/utils/recording-safe-reload';

export default function GlobalError({
  error,
  reset
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  // Detect chunk loading errors and module import failures
  const isChunkError =
    error.message?.includes('Loading chunk') ||
    error.message?.includes('Failed to fetch dynamically imported module') ||
    error.message?.includes('Unable to preload CSS') ||
    error.message?.includes('Failed to import') ||
    error.message?.includes('ChunkLoadError');

  useEffect(() => {
    // Log critical error only if it's not a chunk error (those are expected during deployments)
    if (!isChunkError) {
      globalErrorHandler.reportError(error, {
        digest: error.digest,
        page: 'app/global-error.tsx',
        severity: 'critical'
      });
    }
  }, [error, isChunkError]);

  // Auto-reload for chunk errors (suppressed when a recording is active)
  useEffect(() => {
    if (isChunkError) {
      recordingSafeReload(2000, error);
    }
  }, [isChunkError, error]);

  return (
    <html>
      <body>
        <div className="bg-surface-subtle flex min-h-screen items-center justify-center p-4">
          <div className="bg-card w-full max-w-md rounded-lg p-6 shadow-lg">
            {isChunkError ? (
              <>
                <div className="mb-4 flex items-center justify-center">
                  <Loader2 className="text-info h-12 w-12 animate-spin" />
                </div>
                <h1 className="mb-2 text-center text-2xl font-bold">
                  Updating Application
                </h1>
                <p className="text-foreground-secondary mb-6 text-center">
                  A new version is available. The page will refresh
                  automatically...
                </p>
                <div className="bg-info-subtle rounded p-3">
                  <p className="text-info-subtle-foreground text-center text-sm">
                    Please wait while we load the latest updates.
                  </p>
                </div>
              </>
            ) : (
              <>
                <div className="mb-4 flex items-center justify-center">
                  <AlertOctagon className="text-error h-12 w-12" />
                </div>
                <h1 className="mb-2 text-center text-2xl font-bold">
                  Critical Error
                </h1>
                <p className="text-foreground-secondary mb-6 text-center">
                  Something went seriously wrong. We apologize for the
                  inconvenience.
                </p>
                <div className="bg-muted mb-6 rounded p-3">
                  <p className="text-foreground-secondary font-mono text-sm break-words">
                    {error.message || 'An unexpected error occurred'}
                  </p>
                </div>
                <button
                  onClick={reset}
                  className="bg-info text-info-foreground hover:bg-info/90 flex w-full items-center justify-center gap-2 rounded px-4 py-2 transition-colors"
                >
                  <RefreshCw className="h-4 w-4" />
                  Try Again
                </button>
                {error.digest && (
                  <p className="text-muted-foreground mt-4 text-center text-xs">
                    Error ID: {error.digest}
                  </p>
                )}
              </>
            )}
          </div>
        </div>
      </body>
    </html>
  );
}
